import React, { useState, useEffect } from "react";
import Login from '../components/login';
import './App.css';
import ViewMyTimesheets from "../components/viewMyTimesheets";
import ViewOtherTimesheets from "../components/viewOtherTimesheets";
import PayoutFunds from "../components/payoutFunds";
import { baseUrl } from '../shared';
import { fmtMoney } from '../shared';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PostAddIcon from '@mui/icons-material/PostAdd';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PaymentsIcon from '@mui/icons-material/Payments';
import LoginIcon from '@mui/icons-material/Login';

// --- small presentational helpers (soft-panel design system) ---

function StatTile({ label, value, tone = 'primary' }) {
  const color = {
    primary: 'var(--primary)',
    pass: 'var(--pass)',
    warn: 'var(--warn)',
    info: 'var(--info)',
  }[tone] || 'var(--primary)';
  return (
    <Box
      sx={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r)',
        boxShadow: 'var(--shadow-sm)',
        p: 2,
        height: '100%',
      }}
    >
      <Typography sx={{ color: 'var(--muted)', fontSize: 12, fontWeight: 600, letterSpacing: '.3px', textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Typography className="app-mono" sx={{ color, fontSize: 26, fontWeight: 700, mt: 0.5, lineHeight: 1.1 }}>
        {value}
      </Typography>
    </Box>
  );
}

function ActionCard({ icon, title, subtitle, onClick }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea onClick={onClick} sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 'var(--r-sm)',
            background: 'var(--primary-tint)',
            color: 'var(--primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 16, color: 'var(--text)' }}>{title}</Typography>
        <Typography sx={{ color: 'var(--muted)', fontSize: 13 }}>{subtitle}</Typography>
      </CardActionArea>
    </Card>
  );
}

function TimesheetView() {
  const [openLogin, setOpenLogin] = useState(false);
  const [showMyTimesheets, setShowMyTimesheets] = useState(false);
  const [showOtherTimesheets, setShowOtherTimesheets] = useState(false);
  const [showPayoutFunds, setShowPayoutFunds] = useState(false);

  // Dashboard summary data (role-based).
  const [consultTotals, setConsultTotals] = useState([]);
  const [payrollTotals, setPayrollTotals] = useState([]);

  const loggedIn = !!localStorage.getItem('username');
  const isPayroll = localStorage.getItem('isPayroll') === '1';
  const isConsultant = localStorage.getItem('isConsultant') === '1';
  const canApprove =
    localStorage.getItem('canApprovePayroll') === '1' || localStorage.getItem('canApproveConsultant') === '1';
  const canPayout = localStorage.getItem('canPayout') === '1';
  const name = (localStorage.getItem('name') || '').replace(/"/g, '');

  const authHeaders = {
    'Content-Type': 'application/json',
    username: localStorage.getItem('username'),
    bToken: localStorage.getItem('bToken'),
  };

  useEffect(() => {
    if (!loggedIn) return;
    if (isConsultant) {
      fetch(baseUrl + 'pandt/ownTotals', { method: 'POST', mode: 'cors', headers: authHeaders })
        .then((r) => r.json())
        .then((d) => { if (d.status !== 401) setConsultTotals(d.result || []); })
        .catch(() => {});
    }
    if (isPayroll) {
      fetch(baseUrl + 'payroll/ownTotals', { method: 'POST', mode: 'cors', headers: authHeaders })
        .then((r) => r.json())
        .then((d) => { if (d.status !== 401) setPayrollTotals(d.result || []); })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openLoginPage = () => setOpenLogin((v) => !v);
  const returnToHome = () => {
    setShowMyTimesheets(false);
    setShowOtherTimesheets(false);
    setShowPayoutFunds(false);
  };

  const inSubView = showMyTimesheets || showOtherTimesheets || showPayoutFunds;

  // ---- logged out ----
  if (!loggedIn) {
    return (
      <>
        <Dialog open={openLogin} onClose={openLoginPage} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>Sign in to Timesheet</DialogTitle>
          <DialogContent>
            <Login />
          </DialogContent>
        </Dialog>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 4, md: 10 } }}>
          <Card sx={{ maxWidth: 420, width: '100%', p: 4, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Timesheet</Typography>
            <Typography sx={{ color: 'var(--muted)', mb: 3 }}>
              Log your hours, track approvals and manage payouts.
            </Typography>
            <Button variant="contained" size="large" startIcon={<LoginIcon />} onClick={openLoginPage}>
              Login
            </Button>
          </Card>
        </Box>
      </>
    );
  }

  // ---- logged in: sub-view active ----
  if (inSubView) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={returnToHome} sx={{ mb: 2 }} color="inherit" variant="outlined">
          Back to dashboard
        </Button>
        {showMyTimesheets && <ViewMyTimesheets />}
        {showOtherTimesheets && <ViewOtherTimesheets />}
        {showPayoutFunds && <PayoutFunds />}
      </Box>
    );
  }

  // ---- logged in: dashboard ----
  const approvedSum = consultTotals[0]?.sum ?? 0;
  const payoutSum = consultTotals[1]?.sum ?? 0;
  const balance = approvedSum - payoutSum;
  const thisWeek = payrollTotals[0];
  const lastWeek = payrollTotals[1];

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Welcome back{name ? `, ${name}` : ''}
      </Typography>
      <Typography sx={{ color: 'var(--muted)', mb: 3 }}>Here is your timesheet summary.</Typography>

      {/* KPI tiles */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {isPayroll && (
          <>
            <Grid item xs={6} md={3}>
              <StatTile label={`${thisWeek?.workWeek || 'This week'} hours`} value={thisWeek?.hoursWorkedBilled ?? 0} tone="primary" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile label={`${lastWeek?.workWeek || 'Last week'} hours`} value={lastWeek?.hoursWorkedBilled ?? 0} tone="info" />
            </Grid>
          </>
        )}
        {isConsultant && (
          <>
            <Grid item xs={6} md={3}>
              <StatTile label="Approved total" value={fmtMoney(approvedSum)} tone="pass" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile label="Paid out" value={fmtMoney(payoutSum)} tone="info" />
            </Grid>
            <Grid item xs={6} md={3}>
              <StatTile label="Balance owed" value={fmtMoney(balance)} tone={balance > 0 ? 'warn' : 'pass'} />
            </Grid>
          </>
        )}
      </Grid>

      {/* Quick actions */}
      <Typography sx={{ fontWeight: 700, color: 'var(--muted)', fontSize: 13, textTransform: 'uppercase', letterSpacing: '.4px', mb: 1.5 }}>
        Quick actions
      </Typography>
      <Grid container spacing={2}>
        {(isPayroll || isConsultant) && (
          <Grid item xs={12} sm={6} md={4}>
            <ActionCard
              icon={<PostAddIcon />}
              title="View / Add Timesheets"
              subtitle="Log hours and review your entries by day"
              onClick={() => setShowMyTimesheets(true)}
            />
          </Grid>
        )}
        {canApprove && (
          <Grid item xs={12} sm={6} md={4}>
            <ActionCard
              icon={<FactCheckIcon />}
              title="Approve Timesheets"
              subtitle="Review and approve time for other users"
              onClick={() => setShowOtherTimesheets(true)}
            />
          </Grid>
        )}
        {canPayout && (
          <Grid item xs={12} sm={6} md={4}>
            <ActionCard
              icon={<PaymentsIcon />}
              title="Payout Money"
              subtitle="Record payouts for consultants"
              onClick={() => setShowPayoutFunds(true)}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default TimesheetView;
